<!--
@component
MatchesWidget (Embed)

Embed component used by the matches-widget route for syndication.

Reads an optional `?country=<slug>` query parameter from the page URL
and dispatches to either `AllMatches` (no param) or `TeamMatches`
(country param present). A spinner is shown until hydration so we
never flash the wrong list.
-->

<script lang="ts">
  import { onMount } from 'svelte';
  import Logo from '$lib/components/common/Menu/Logo/index.svelte';
  import Spinner from '$lib/components/common/Spinner/index.svelte';
  import AllMatches from './AllMatches.svelte';
  import TeamMatches from './TeamMatches.svelte';
  import { Block } from '@reuters-graphics/graphics-components';
  import { homepage } from '$pkg';
  import { appState } from '$lib/stores/index.svelte';
  import getTeamPath from '$utils/getTeamPath';

  // Query params are only known after hydration (the embed route is
  // statically prerendered, so SSR / first-paint has no access to the
  // actual URL the user visited). Read them from `window.location` on
  // mount — more reliable inside iframes than `page.url.searchParams`.
  let mounted = $state(false);
  let countrySlug = $state('');
  let hideLogo = $state(true);

  onMount(() => {
    const params = new URLSearchParams(window.location.search);
    countrySlug = params.get('country') ?? '';
    const hl = params.get('logo');
    hideLogo = !(hl === 'false' || hl === '0' || hl === '');
    mounted = true;
  });

  const hasCountryParam = $derived(mounted && countrySlug.length > 0);

  /**
   * Logo link.
   *
   * When the widget is actually rendered inside a third-party iframe
   * (`appState.embed`), point at production via the package
   * `homepage` — same approach used in `getMatchPath` / `getTeamPath`.
   *
   * Otherwise let `getTeamPath` produce a SvelteKit-resolved relative
   * URL for the team page, or fall back to the in-app root via
   * `appState.linkBase`.
   */
  const linkHref = $derived.by(() => {
    if (appState.embed) {
      const base = homepage.replace(/\/$/, '');
      return hasCountryParam ? `${base}/teams/${countrySlug}/` : homepage;
    }
    if (hasCountryParam) {
      return getTeamPath({ slug: countrySlug })?.url ?? `${appState.linkBase}/`;
    }
    return `${appState.linkBase}/`;
  });
</script>

<Block
  width="fluid"
  id="matches-widget"
  class="fmx-auto flex justify-left items-center"
>
  {#if !hideLogo}
    <div class="logo fmt-1">
      <a href={linkHref} target="_top">
        <Logo />
      </a>
    </div>
  {/if}

  {#if !mounted}
    <div class="spinner-wrapper" aria-busy="true" aria-live="polite">
      <Spinner size={32} label="Loading matches" class="" />
    </div>
  {:else if hasCountryParam}
    <TeamMatches {countrySlug} />
  {:else}
    <AllMatches />
  {/if}
</Block>

<style lang="scss">
  a {
    text-decoration: none !important;
  }

  :global(.logo) {
    flex: 0 0 auto;
  }

  :global(#matches-widget) {
    @media (max-width: 600px) {
      flex-direction: column;
    }
  }

  // Reserve vertical space so layout doesn't shift when matches arrive.
  .spinner-wrapper {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 90px;
  }
</style>
